import React from 'react';
import { Container, Box, Typography, useMediaQuery, useTheme } from '@mui/material';

const SERVER = process.env.REACT_APP_SERVER;
const aboutFileUrl = (fileName) => `${SERVER}/api/upload/homeFiles/${fileName}`;

const aboutImg = aboutFileUrl('about_img_wide.png');
const aboutIcon1 = aboutFileUrl('about_icon1.svg');
const aboutIcon2 = aboutFileUrl('about_icon2.svg');
const aboutIcon3 = aboutFileUrl('about_icon3.svg');

// 연혁 데이터 배열 (같은 날짜에 여러 항목이 있는 경우 texts 배열에 추가)
const historyData = [
    { date: '2025.11', texts: ['연구 전담부서 개소'] },
    { date: '2025.09', texts: ['커뮤니티 리빙랩 프로젝트 선정'] },
    { date: '2025.08', texts: ['대구한의대학교 신입생, 성인학습자\n실태 및 의식조사 연구 용역'] },
    { date: '2025.07', texts: ['대구광역시 예비사회적기업 지정'] },
    {
        date: '2025.05',
        texts: [
            '대구동부교육지원청 Wee센터\n집단상담 프로그램 위탁개발 연구 용역',
            '교통안전공단 고령운수종사자 자격유지검사\n개선 방안 마련 연구 용역',
        ],
    },
    {
        date: '2025.04',
        texts: ['전국민마음투자지원사업, 지역사회서비스\n투자사업 바우처 제공기관 등록'],
    },
    { date: '2025.03', texts: ['(주) 가람마인드 설립'] },
];

const About = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // 에셋 경로 설정 (SVG 이미지 사용)
    const assets = {
        heroImg: aboutImg,
        icon1: aboutIcon1,
        icon2: aboutIcon2,
        icon3: aboutIcon3,
    };

    return (
        <Box>
            {/* 1. Hero Section */}
            <Box
                sx={{
                    height: { xs: '70vh', md: '500px' }, // 높이 조절
                    minHeight: '400px',
                    width: '100%',
                    backgroundImage: `url(${assets.heroImg})`,
                    backgroundColor: '#f5f5f5',
                    backgroundSize: 'cover', // 이미지를 잘라서 채움
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                    }}
                />

                <Container sx={{ position: 'relative', zIndex: 2 }}>
                    <Box
                        sx={{
                            color: '#000',
                        }}
                    >
                        <Typography
                            sx={{
                                wordBreak: 'keep-all',
                                fontWeight: '800',
                                letterSpacing: '0.06em',
                                lineHeight: 1.2,
                                mb: 1,
                            }}
                        >
                            ABOUT
                        </Typography>
                        <Typography
                            variant={isMobile ? 'h4' : 'h2'}
                            sx={{
                                wordBreak: 'keep-all',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2,
                            }}
                        >
                            정신건강 서비스가 <br />
                            가장 필요한 사람에게
                        </Typography>
                        <Typography
                            variant={isMobile ? 'h4' : 'h2'}
                            sx={{
                                wordBreak: 'keep-all',
                                fontWeight: 'bold',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2,
                                mb: 1,
                            }}
                        >
                            먼저 닿는 사회를 만듭니다.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* 2. Mission Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 }, textAlign: 'center' }}>
                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: '1.8rem',
                        wordBreak: 'keep-all',
                        lineHeight: 1.4,
                        letterSpacing: '-0.04em',
                    }}
                >
                    디지털 기술을 통해 정신건강 사각지대를 해소하는
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: '2rem',
                        wordBreak: 'keep-all',
                        lineHeight: 1.4,
                        letterSpacing: '-0.04em',
                    }}
                >
                    심리 기반 딥테크 스타트업
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: '#666', fontSize: '1.1rem', letterSpacing: '-0.04em' }}
                >
                    기술로 마음을 잇고, 누구나 닿을 수 있는 정신건강을 만듭니다.
                </Typography>

                {/* 3. Service Cards Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'stretch',
                        gap: 3,
                        mt: { xs: 5, md: 10 },
                        pb: { xs: 0, sm: 8 },
                    }}
                >
                    {[
                        {
                            icon: assets.icon1,
                            title: 'AI 심리상담 대화모델',
                            desc: '심리 이론을 학습한 AI로 듣고 공감하며 흐름을 이어가는 상담',
                        },
                        {
                            icon: assets.icon2,
                            title: '데이터 기반 서비스 연결',
                            desc: '데이터 기반 서비스 연결 더 정확한 케어의 시작',
                        },
                        {
                            icon: assets.icon3,
                            title: '전문 심리 서비스',
                            desc: '기준이 다른 전문 심리 서비스 신뢰로 완성되는 심리 케어',
                        },
                    ].map((item, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                width: '100%',
                                mt: { xs: 0, md: idx === 1 ? '10%' : 0 },
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: '#f8f9fa',
                                    p: 5,
                                    borderRadius: 8,
                                    height: '100%',
                                    boxSizing: 'border-box',
                                    textAlign: 'left',
                                    transition: '0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={item.icon}
                                    alt={item.title}
                                    sx={{ width: 60, height: 60, mb: 3 }}
                                />
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                    {item.title}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{ color: '#666', lineHeight: 1.6, wordBreak: 'keep-all' }}
                                >
                                    {item.desc}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>

            {/* 4. Mission & Vision Section */}
            <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 8, md: 10 },
                        }}
                    >
                        {/* Mission */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', md: 'flex-start' },
                                gap: 4,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#004a7c',
                                    fontWeight: 800,
                                    wordBreak: 'keep-all',
                                    pt: { md: 1 },
                                    width: '100%',
                                }}
                            >
                                기술로 마음을 잇는 서비스
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    maxWidth: { md: '55%' },
                                    width: '100%',
                                    textAlign: 'left',
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#000',
                                        fontSize: '1.2rem',
                                    }}
                                >
                                    Mission
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#444',
                                        wordBreak: 'keep-all',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    심리평가·치료·교육을 디지털로 연결해 실제 변화로 이어지는
                                    <br className="hidden md:block" />
                                    정신건강 서비스를 만듭니다.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Vision */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', md: 'flex-start' },
                                gap: 4,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#004a7c',
                                    fontWeight: 800,
                                    wordBreak: 'keep-all',
                                    pt: { md: 1 },
                                    width: '100%',
                                }}
                            >
                                누구나 닿을 수 있는 정신건강을 위해
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    maxWidth: { md: '55%' },
                                    width: '100%',
                                    textAlign: 'left',
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#000',
                                        fontSize: '1.2rem',
                                    }}
                                >
                                    Vision
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#444',
                                        wordBreak: 'keep-all',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    정신건강이 특정 사람의 특권이 아닌, 모두에게 접근 가능한
                                    <br className="hidden md:block" />
                                    기본 인프라가 되는 세상.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* 5. Timeline (History) Section */}
            <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 10, md: 15 } }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography
                            variant={isMobile ? 'h5' : 'h4'}
                            sx={{ fontWeight: 800, mb: 2 }}
                        >
                            가람마인드는 오늘도
                            <br />
                            우리가 필요한 사람에게 다가갑니다.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            mx: 'auto',
                            width: 'fit-content',
                        }}
                    >
                        {historyData.map((row, rowIndex) => (
                            // 각 연월(row) 단위의 블록
                            <Box
                                key={rowIndex}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                {row.texts.map((text, textIndex) => (
                                    // 내용(text) 단위의 행
                                    <Box
                                        key={textIndex}
                                        sx={{ display: 'flex', alignItems: 'flex-start' }}
                                    >
                                        {/* 1. 날짜 영역 (고정 넓이) */}
                                        <Box
                                            sx={{
                                                width: { xs: '70px', sm: '90px' },
                                                flexShrink: 0,
                                            }}
                                        >
                                            {/* 첫 번째 항목일 때만 날짜를 표시합니다 */}
                                            {textIndex === 0 && (
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="900"
                                                    sx={{ color: '#045584', pt: 0.2 }}
                                                >
                                                    {row.date}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* 2. 동그라미 아이콘 영역 */}
                                        <Box
                                            sx={{
                                                px: { xs: 1.5, sm: 3 },
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    border: '5px solid #045584', // 두꺼운 테두리로 이미지의 아이콘 구현
                                                    bgcolor: 'transparent',
                                                    mt: 0.8, // 텍스트 라인과 높이 맞춤
                                                }}
                                            />
                                        </Box>

                                        {/* 3. 텍스트 내용 영역 (나머지 공간 차지) */}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: '#555555',
                                                    lineHeight: 1.6,
                                                    wordBreak: 'keep-all', // 단어 단위로 줄바꿈
                                                    whiteSpace: 'pre-line', // \n을 인식하여 줄바꿈
                                                    fontSize: { xs: '0.8rem', md: '1.15rem' },
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default About;
